import { useCallback, useEffect, useState } from 'react'
import { paperAPI } from '../../services/api'
import type { PaperAccountSummary, PaperEquityCurve, PaperPosition } from '../../types'
import type { CurvePoint } from '../dashboard/EquityCurveChart'

// The positions endpoint's ceiling. The forward test's kill rule looks at 100
// trades, so a list this long covers the test's whole life.
export const PAPER_CLOSED_LIMIT = 500

const DAY_MS = 86_400_000

/** A calendar date from the API ("2026-09-17") as UTC midnight. */
const dayStart = (isoDate: string) => new Date(`${isoDate}T00:00:00Z`).getTime()

export interface PaperTrade {
  /** Close order, 1 = the first trade the forward test closed. */
  n: number
  position: PaperPosition
  closedAt: number | null
  /** Net of both fees, percent of what the position cost. */
  netPct: number
  win: boolean
  r: number | null
}

export interface PaperRecord {
  account: PaperAccountSummary
  /** The account's own totals; `trades` may hold fewer (PAPER_CLOSED_LIMIT). */
  closed: number
  wins: number
  losses: number
  winRate: number | null
  avgWinPct: number | null
  avgLossPct: number | null
  avgR: number | null
  /** Trades with an R behind avgR; older API builds send none. */
  rCount: number
  /** One point per daily snapshot, led by the launch at 0%. */
  curve: CurvePoint[]
  /** Deepest fall from a peak of account equity, in percent of that peak. */
  maxDrawdownPct: number
  /** Newest close first. */
  trades: PaperTrade[]
}

const mean = (values: number[]) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null)

/**
 * What came out of the paper account's Avg R, in its own words.
 *
 * The forward test books a fee on entry and on exit and no slippage, at a rate
 * the API reports; the signal engine models a different venue at different
 * rates. Both records render through the same "Avg R" tile, so each states its
 * own basis -- otherwise the toggle swaps one definition for another under an
 * unchanged label, which is how a gross number came to sit beside a net one.
 */
export const paperAvgRBasis = (account: { fee_per_side_pct?: number | null }): string => {
  const perSide = account.fee_per_side_pct
  return perSide == null
    ? 'net of fees · per closed trade'
    : `net of ${(perSide * 2).toFixed(2)}% fees · per closed trade`
}

/**
 * The forward test's record from its own ledger: closed positions (P&L net of
 * both fees, a win only when it made money, as the backend counts it) and the
 * daily account equity, open positions marked to market. Nothing from the old
 * engine is ever read here.
 */
export const buildPaperRecord = (
  account: PaperAccountSummary,
  equity: PaperEquityCurve | null,
  positions: PaperPosition[]
): PaperRecord => {
  const closedPositions = positions
    .filter((p) => p.status === 'closed')
    .sort((a, b) => {
      const byDate = (a.exit_date ?? '').localeCompare(b.exit_date ?? '')
      return byDate !== 0 ? byDate : a.id - b.id
    })

  // A capped list holds the newest trades, so numbering starts after the ones left out.
  const skipped = Math.max(0, account.closed_trades - closedPositions.length)
  const trades: PaperTrade[] = closedPositions.map((position, index) => ({
    n: skipped + index + 1,
    position,
    closedAt: position.exit_date ? dayStart(position.exit_date) : null,
    netPct: position.pnl_pct ?? 0,
    win: position.win ?? (position.net_pnl ?? 0) > 0,
    r: position.r_multiple ?? null,
  }))

  const winMoves = trades.filter((t) => t.win).map((t) => t.netPct)
  const lossMoves = trades.filter((t) => !t.win).map((t) => t.netPct)
  const rValues = trades.map((t) => t.r).filter((r): r is number => r != null)

  const start = equity?.starting_balance ?? account.starting_balance
  const launched = account.launched_on ? dayStart(account.launched_on) : null
  const snapshots = equity?.points ?? []
  const curve: CurvePoint[] =
    launched == null || snapshots.length === 0
      ? []
      : [
          { n: 0, cumulative: 0, pnl: null, symbol: null, win: null, time: launched, equity: start },
          ...snapshots.map((point) => {
            const time = dayStart(point.as_of)
            return {
              n: Math.round((time - launched) / DAY_MS),
              cumulative: start ? (point.equity / start - 1) * 100 : 0,
              pnl: null,
              symbol: null,
              win: null,
              time,
              equity: point.equity,
              openPositions: point.open_positions,
            }
          }),
        ]

  return {
    account,
    // Counts come from the account itself, so the denominator never depends
    // on how many trades the list endpoint returned.
    closed: account.closed_trades,
    wins: account.wins,
    losses: account.losses,
    winRate: account.closed_trades ? account.win_rate : null,
    avgWinPct: mean(winMoves),
    avgLossPct: mean(lossMoves),
    avgR: mean(rValues),
    rCount: rValues.length,
    curve,
    maxDrawdownPct: Math.max(0, account.drawdown_pct, ...snapshots.map((p) => p.drawdown_pct)),
    trades: trades.reverse(),
  }
}

/** Loads the paper account, its daily equity and every closed trade. */
export const usePaperRecord = () => {
  const [record, setRecord] = useState<PaperRecord | null>(null)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let active = true
    setFailed(false)
    Promise.all([
      paperAPI.account(),
      // A missing curve only blanks the chart; the trades still stand.
      paperAPI.equityCurve(2000).catch(() => null),
      paperAPI.positions({ status: 'closed', limit: PAPER_CLOSED_LIMIT }),
    ])
      .then(([account, equity, positions]) => {
        if (active) setRecord(buildPaperRecord(account, equity, positions))
      })
      .catch((error) => {
        console.error('Paper record failed to load:', error)
        if (active) setFailed(true)
      })
    return () => {
      active = false
    }
  }, [attempt])

  const reload = useCallback(() => setAttempt((n) => n + 1), [])
  return { record, failed, reload }
}
